import {
  ApolloError,
  LazyQueryExecFunction,
  MutationFunctionOptions,
  OperationVariables,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import { displayToast, updateToast } from 'components/system/Toast';
import { TToastData } from 'components/system/Toast/toast.types';
import { GraphqlErrorCodeEnum } from 'constants/graphql';
import { Types } from 'mongoose';
import { useForm } from 'react-hook-form';
import { Id } from 'react-toastify';
import { TOperationProps } from 'types/apollo-types';
import { getDateWithTimezone } from 'utils/date/getDateWithTimezone';
import useYupValidationResolver from 'utils/useYupValidationResolver';
import { GET_EXAM_STATUS, UPDATE_EXAM } from './examForms.api';
import {
  IExamFormHookProps,
  IExamStatusQueryData,
  IFormFieldValues,
  IUpdateExamMutationData,
  TExamStatusData,
  TUpdateExamData,
  TUpdateExamResponse,
} from './examForms.types';
import { getValidationSchema } from './examForms.yup';

export const onUpdateExamError = (responseError: ApolloError, toastId?: Id) => {
  const isResourceNotFound =
    responseError?.graphQLErrors?.[0]?.message ===
    GraphqlErrorCodeEnum.RESOURCE_NOT_FOUND;

  const extensionErrorMessage =
    responseError?.graphQLErrors?.[0]?.extensions?.message;
  const errorMessage =
    extensionErrorMessage && typeof extensionErrorMessage === 'string'
      ? extensionErrorMessage
      : 'Please try again later.';

  const description = isResourceNotFound
    ? 'The exam could not be found.'
    : errorMessage;

  const toastData: TToastData = {
    type: 'error',
    title: 'Error updating exam',
    description,
  };

  if (toastId) {
    updateToast(toastId, toastData);
  } else {
    displayToast(toastData);
  }
};

export const useUpdateExam = (
  examId?: Types.ObjectId,
  options?: MutationFunctionOptions<TUpdateExamData>,
) => {
  const [updateExam, mutationData]: [TOperationProps, IUpdateExamMutationData] =
    useMutation(UPDATE_EXAM, {
      onError: (requestError) => onUpdateExamError(requestError),
      ...options,
    });

  const { data, loading, error } = mutationData;
  return { updateExam, data, loading, error };
};

export const useExamStatus = () => {
  const [getExamStatus, queryData]: [
    LazyQueryExecFunction<TExamStatusData, OperationVariables>,
    IExamStatusQueryData,
  ] = useLazyQuery(GET_EXAM_STATUS);

  const { data, loading, error } = queryData;

  return { getExamStatus, data, loading, error };
};

export const useExamForm = ({
  exam,
  onCompleteSubmission,
  onBeforeSubmission,
  nextStatus,
}: IExamFormHookProps) => {
  const formReturn = useForm<IFormFieldValues>({
    mode: 'onBlur',
    resolver: useYupValidationResolver(getValidationSchema()),
  });

  const {
    updateExam,
    loading,
    error: updateExamError,
  } = useUpdateExam(exam?._id);

  const { handleSubmit } = formReturn;

  const onSubmit = async (formData: IFormFieldValues) => {
    onBeforeSubmission?.();
    const examDate = formData.examDate
      ? getDateWithTimezone(formData.examDate).set('hour', 12).toDate()
      : null;

    const { ceaLevel, glucoseLevel, ...restFormData } = formData;

    const informationData = {
      ...restFormData,
      examDate,
      physician: formData.physician?.value || null,
      priority: formData.priority?.value || '',
      histologicalResult: formData.histologicalResult?.value ?? null,
      laboratoryResult: {
        glucoseLevel: glucoseLevel ? parseInt(glucoseLevel, 10) : null,
        ceaLevel: ceaLevel ? parseInt(ceaLevel, 10) : null,
      },
      ...(nextStatus && { status: nextStatus }),
    };

    // update exam in database
    await updateExam({
      variables: {
        updateExamId: exam?._id,
        updateExamRecord: informationData,
      },
      onCompleted: (data: TUpdateExamResponse) => {
        void onCompleteSubmission(data);
      },
    });
  };

  return {
    isLoading: loading,
    handleSubmit: handleSubmit(onSubmit),
    formReturn,
    updateExamError,
  };
};
